/**
 * HTTP API client for TarifData backend (Telegram Mini App).
 */

import {
  ApiError,
  createSessionClient,
  type SessionRequestOptions,
  type TokenStore,
} from '@tarifdata/api-client';

const ENV_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const DEFAULT_API_BASE_URL =
  typeof window !== 'undefined' && window.location.hostname === 'tma.tarifdata.com'
    ? 'https://api.tarifdata.com/api'
    : '/api';
const API_BASE_URL = (ENV_API_BASE_URL || DEFAULT_API_BASE_URL).replace(/\/+$/, '');

const tokenStore: TokenStore = {
  getAccessToken: () => localStorage.getItem('tma_token'),
  getRefreshToken: () => localStorage.getItem('tma_refresh_token'),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('tma_token', accessToken);
    if (refreshToken) localStorage.setItem('tma_refresh_token', refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem('tma_token');
    localStorage.removeItem('tma_refresh_token');
    localStorage.removeItem('tma_user');
  },
};

const sessionClient = createSessionClient({
  baseUrl: API_BASE_URL,
  tokenStore,
  onSessionExpired: () => {
    window.dispatchEvent(new CustomEvent('tarifdata:session-expired'));
  },
});

export const api = {
  get: <T>(endpoint: string, options?: SessionRequestOptions) =>
    sessionClient.get<T>(endpoint, options),

  post: <T>(endpoint: string, body?: unknown, options?: SessionRequestOptions) =>
    sessionClient.post<T>(endpoint, body, options),

  patch: <T>(endpoint: string, body: unknown, options?: SessionRequestOptions) =>
    sessionClient.patch<T>(endpoint, body, options),

  delete: <T>(endpoint: string, options?: SessionRequestOptions) =>
    sessionClient.delete<T>(endpoint, options),

  upload: async <T>(endpoint: string, file: File): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file);
    return sessionClient.request<T>(endpoint, {
      method: 'POST',
      body: formData,
    });
  },

  download: async (endpoint: string, filename: string) => {
    const response = await sessionClient.raw(endpoint);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(anchor);
  },
};

export const refreshAccessToken = () => sessionClient.refreshAccessToken();

export { ApiError };
